import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  FileText,
  LayoutDashboard,
  ScrollText,
  ShieldCheck,
  Settings,
  LogOut,
  Stethoscope,
  Target,
  Workflow as WorkflowIcon,
  FolderKanban,
  Inbox,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
  ChevronDown,
  UserCog,
  KeyRound,
  Gauge,
  Sparkles,
  Activity,
  Newspaper,
  Calendar,
  Cpu,
  UserCheck,
  Gavel,
  Building,
  FileBadge,
  BarChart3,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { ROLE_RANK, type UserRole } from '@praxara/shared/constants';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { BrandMark } from '@/components/brand/BrandMark';
import { CommandPalette, useCommandPalette } from '@/components/layout/CommandPalette';
import { Search } from 'lucide-react';
import { useEventStream } from '@/hooks/useEventStream';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { WhatsNewBadge } from '@/components/layout/WhatsNewBadge';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  minRole?: UserRole;
  section?: string;
  /** When true, only `user.isSuperAdmin === true` users see this entry. */
  superAdminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, section: 'Workspace' },
  { to: '/my-queue', label: 'My queue', icon: Inbox, section: 'Workspace' },
  { to: '/projects', label: 'Projects', icon: FolderKanban, section: 'Workspace' },
  { to: '/documents', label: 'Documents', icon: FileText, section: 'Workspace' },
  { to: '/search', label: 'Search', icon: Search, section: 'Workspace' },
  { to: '/workflows', label: 'Workflows', icon: WorkflowIcon, section: 'Workspace' },
  { to: '/pv', label: 'Pharmacovigilance', icon: Stethoscope, section: 'Workspace' },
  { to: '/pv/reporting', label: 'PV Reporting', icon: BarChart3, section: 'Workspace' },
  { to: '/signals', label: 'Signals', icon: Activity, section: 'Workspace' },
  { to: '/prac', label: 'PRAC actions', icon: Gavel, section: 'Workspace' },
  { to: '/regulatory-intel', label: 'Reg-intel feed', icon: Newspaper, section: 'Workspace' },
  { to: '/eurd', label: 'EURD list', icon: Calendar, section: 'Workspace' },
  { to: '/compliance-pack', label: 'Compliance Pack', icon: Sparkles, minRole: 'MANAGER', section: 'Governance' },
  { to: '/compliance', label: 'Compliance scanner', icon: Target, minRole: 'MANAGER', section: 'Governance' },
  { to: '/compliance-register', label: 'Compliance register', icon: ShieldCheck, minRole: 'MANAGER', section: 'Governance' },
  { to: '/audit', label: 'Audit trail', icon: ScrollText, minRole: 'REVIEWER', section: 'Governance' },
  // Lowered to REVIEWER so external advisor / QPPV reviewer accounts can see
  // the AI Act + GDPR governance evidence during demos and audits. The
  // underlying GET endpoints have been lowered to REVIEWER as well; writes
  // (model card creation, DSR status changes) remain ADMIN/MANAGER-only.
  { to: '/admin/ai-governance', label: 'AI Governance', icon: Cpu, minRole: 'REVIEWER', section: 'Governance' },
  { to: '/admin/prompt-versions', label: 'Prompt versions', icon: Gavel, minRole: 'ADMIN', section: 'Governance' },
  { to: '/admin/gdpr', label: 'GDPR', icon: UserCheck, minRole: 'REVIEWER', section: 'Governance' },
  { to: '/admin', label: 'Admin', icon: Settings, minRole: 'ADMIN', section: 'Administration' },
  { to: '/admin/invoices', label: 'Invoices', icon: FileBadge, minRole: 'ADMIN', section: 'Administration' },
  { to: '/admin/diagnostics', label: 'Diagnostics', icon: Cpu, minRole: 'ADMIN', section: 'Administration' },
  { to: '/platform-admin', label: 'Platform overview', icon: BarChart3, section: 'Platform', superAdminOnly: true },
  { to: '/platform-admin/tenants', label: 'Tenants', icon: Building, section: 'Platform', superAdminOnly: true },
  { to: '/platform-admin/subscriptions', label: 'Subscriptions', icon: FileBadge, section: 'Platform', superAdminOnly: true },
];

export function AppShell() {
  const { user, tenant, clear, refreshToken } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar, theme, toggleTheme } = useThemeStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const palette = useCommandPalette();
  useEventStream();

  async function handleLogout() {
    try {
      if (refreshToken) await api.post('/api/auth/logout', { refreshToken });
    } catch {
      /* ignore */
    }
    clear();
    navigate('/login', { replace: true });
  }

  if (!user || !tenant) return null;

  const visibleNav = NAV.filter(
    (item) =>
      (!item.minRole || ROLE_RANK[user.role] >= ROLE_RANK[item.minRole]) &&
      (!item.superAdminOnly || user.isSuperAdmin),
  );
  const sections = Array.from(new Set(visibleNav.map((i) => i.section ?? 'Main')));

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          'relative hidden shrink-0 flex-col border-r bg-card transition-[width] duration-200 md:flex',
          sidebarCollapsed ? 'w-[64px]' : 'w-[248px]',
        )}
      >
        {/* Brand header */}
        <div className={cn('flex h-16 items-center border-b', sidebarCollapsed ? 'justify-center' : 'px-5')}>
          {sidebarCollapsed ? (
            <BrandMark variant="icon" size="sm" />
          ) : (
            <BrandMark size="md" />
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-6 overflow-y-auto p-3">
          {sections.map((section) => (
            <div key={section}>
              {!sidebarCollapsed && (
                <div className="mb-1.5 px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {section}
                </div>
              )}
              <ul className="space-y-0.5">
                {visibleNav.filter((i) => (i.section ?? 'Main') === section).map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                          sidebarCollapsed && 'justify-center',
                          isActive
                            ? 'bg-primary/10 text-primary dark:bg-primary/20'
                            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Tenant badge + theme + collapse */}
        <div className={cn('border-t p-3 space-y-2', sidebarCollapsed && 'flex flex-col items-center')}>
          {!sidebarCollapsed && (
            <div className="rounded-md border bg-muted/30 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tenant</div>
              <div className="truncate text-sm font-medium">{tenant.name}</div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                {tenant.plan} · {tenant.gxpValidated ? 'GxP validated' : 'Pre-production · not for clinical use'}
              </div>
            </div>
          )}

          <div className={cn('flex gap-1', sidebarCollapsed && 'flex-col')}>
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/95 px-6 backdrop-blur">
          {/* Quick search trigger */}
          <button
            onClick={() => palette.setOpen(true)}
            className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border bg-card px-3 text-sm text-muted-foreground transition-colors hover:bg-accent/40"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search documents, workflows, commands...</span>
            <kbd className="ml-2 hidden items-center gap-1 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium sm:flex">
              Ctrl K
            </kbd>
          </button>
          <div className="flex-1" />
          <div className="hidden items-center gap-1 lg:flex">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                tenant.gxpValidated
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400'
                  : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-400'
              )}
              title={tenant.gxpValidated ? 'GxP-validated environment' : 'Pre-production tenant — not for clinical use'}
            >
              <ShieldCheck className="h-3 w-3" />
              {tenant.gxpValidated ? 'GxP' : 'Pre-prod'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground" title="21 CFR Part 11 e-signatures + audit chain enabled">
              21 CFR Part 11
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground" title="EU AI Act high-risk classification + model cards">
              EU AI Act
            </span>
          </div>
          <WhatsNewBadge />
          <NotificationBell />
          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-md border bg-card py-1.5 pl-1.5 pr-3 text-sm transition-colors hover:bg-accent"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                {user.firstName[0]}
                {user.lastName[0]}
              </div>
              <span className="font-medium">{user.firstName}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{user.role}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full z-20 mt-2 w-60 animate-slide-up rounded-md border bg-popover p-1.5 shadow-popover">
                  <div className="border-b px-3 py-2">
                    <div className="truncate text-sm font-medium">{user.firstName} {user.lastName}</div>
                    <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                  </div>
                  <MenuLink to="/settings/profile" icon={UserCog} label="Profile" onClick={() => setUserMenuOpen(false)} />
                  <MenuLink to="/settings/security" icon={KeyRound} label="Security + sessions" onClick={() => setUserMenuOpen(false)} />
                  <MenuLink to="/settings/team" icon={UserCog} label="Team" onClick={() => setUserMenuOpen(false)} />
                  <MenuLink to="/settings/usage" icon={Gauge} label="Usage + billing" onClick={() => setUserMenuOpen(false)} />
                  <MenuLink to="/help" icon={UserCog} label="Help & getting started" onClick={() => setUserMenuOpen(false)} />
                  {user.isSuperAdmin && (
                    <>
                      <div className="my-1 border-t" />
                      <MenuLink to="/platform-admin" icon={ShieldCheck} label="Platform admin" onClick={() => setUserMenuOpen(false)} />
                    </>
                  )}
                  <div className="my-1 border-t" />
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <div className="mx-auto max-w-7xl p-6">
          <Outlet />
        </div>
      </main>
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </div>
  );
}

function MenuLink({
  to,
  icon: Icon,
  label,
  onClick,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      {label}
    </NavLink>
  );
}
